import { Validators } from '@angular/forms';

export type ProjectFieldType = 'text' | 'email' | 'url' | 'number' | 'textarea' | 'checkbox' | 'select' | 'multiselect' | 'date' | 'file';
export type ProjectFormTab = 'details' | 'team' | 'goals' | 'gallery';
export type ProjectFormGroupName = 'projectInfo' | 'projectDetails' | 'socialMedia' | 'projectTimeline' | 'projectContent' | 'teamOverview' | 'teamMember' | 'partnerOrg' | 'goalsOverview' | 'galleryOverview';

export interface ProjectFormFieldConfig {
  name: string;
  labelKey: string;          // Translation key for label
  type: ProjectFieldType;
  placeholderKey?: string;   // Translation key for placeholder
  defaultValue?: any;
  validators?: any[];
  maxLength?: number;
  tab: ProjectFormTab;
  group: ProjectFormGroupName;
  profileGroup?: ProjectFormGroupName; // Section this field is displayed under in the profile setup dialog, when it differs from `group` (the form-model group)
  sectionTitleKey: string;   // Translation key for section title
  key?: string;              // Key for database mapping of the field
}

/**
 * Project Form Configuration
 * Single source of truth for all form fields.
 * All labels and placeholders use translation keys for multi-tenant, multi-language support.
 */
export const PROJECT_FORM_CONFIG: ProjectFormFieldConfig[] = [
  // ==================== DETAILS TAB ====================
  // Project Info Section
  {
    name: 'projectProfile',
    labelKey: 'settings.project_details_form.project_profile_label',
    type: 'select',
    placeholderKey: 'settings.project_details_form.select_profile',
    defaultValue: null,
    validators: [Validators.required],
    tab: 'details',
    group: 'projectInfo',
    sectionTitleKey: 'settings.project_details_form.project_profile_label',
    key: 'projectProfile'
  },
  {
    name: 'charityUuid',
    labelKey: 'settings.project_details_form.select_charity_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.select_charity_placeholder',
    defaultValue: '',
    validators: [Validators.required],
    tab: 'details',
    group: 'projectInfo',
    sectionTitleKey: 'settings.project_details_form.select_charity_label',
    key: 'charityUuid'
  },

  // Project Details Section
  {
    name: 'projectName',
    labelKey: 'settings.project_details_form.project_name_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.project_name_placeholder',
    defaultValue: '',
    validators: [Validators.required, Validators.maxLength(200)],
    maxLength: 200,
    tab: 'details',
    group: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'projectName'
  },
  {
    name: 'givingLink',
    labelKey: 'settings.project_details_form.giving_link_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.giving_link_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'projectTimeline',
    profileGroup: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'givingLink'
  },
  {
    name: 'cause',
    labelKey: 'settings.project_details_form.project_cause_label',
    type: 'select',
    placeholderKey: 'settings.project_details_form.project_cause_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'cause'
  },
  {
    name: 'category',
    labelKey: 'settings.project_details_form.project_category_label',
    type: 'multiselect',
    placeholderKey: 'settings.project_details_form.project_category_placeholder',
    defaultValue: [],
    tab: 'details',
    group: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'categories'
  },
  {
    name: 'country',
    labelKey: 'settings.project_details_form.project_country_label',
    type: 'multiselect',
    placeholderKey: 'settings.project_details_form.project_country_placeholder',
    defaultValue: [],
    tab: 'details',
    group: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'countriesList'
  },
  {
    name: 'website',
    labelKey: 'settings.project_details_form.project_website_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.project_website_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'projectDetails',
    sectionTitleKey: 'settings.project_details_form.project_details_title',
    key: 'website'
  },

  // Social Media Links Section
  {
    name: 'linkedin',
    labelKey: 'settings.project_details_form.linkedin_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.linkedin_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMedia',
    sectionTitleKey: 'settings.project_details_form.social_media_title',
    key: 'socialMediaLinkedin'
  },
  {
    name: 'twitter',
    labelKey: 'settings.project_details_form.x_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.x_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMedia',
    sectionTitleKey: 'settings.project_details_form.social_media_title',
    key: 'socialMediaTwitter'
  },
  {
    name: 'facebook',
    labelKey: 'settings.project_details_form.facebook_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.facebook_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMedia',
    sectionTitleKey: 'settings.project_details_form.social_media_title',
    key: 'socialMediaFacebook'
  },
  {
    name: 'instagram',
    labelKey: 'settings.project_details_form.instagram_label',
    type: 'url',
    placeholderKey: 'settings.project_details_form.instagram_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMedia',
    sectionTitleKey: 'settings.project_details_form.social_media_title',
    key: 'socialMediaInstagram'
  },

  // Project Timeline / Organization Address Section
  {
    name: 'targetPopulation',
    labelKey: 'settings.project_details_form.target_population_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.target_population_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'targetPopulation'
  },
  {
    name: 'startDate',
    labelKey: 'settings.project_details_form.start_date_label',
    type: 'date',
    placeholderKey: 'settings.project_details_form.start_date_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'startDate'
  },
  {
    name: 'endDate',
    labelKey: 'settings.project_details_form.end_date_label',
    type: 'date',
    placeholderKey: 'settings.project_details_form.end_date_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'endDate'
  },
  {
    name: 'currency',
    labelKey: 'settings.project_details_form.project_currency_label',
    type: 'select',
    placeholderKey: 'settings.project_details_form.project_currency_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'amountRaisedToDateCurrency'
  },
  {
    name: 'amountRaised',
    labelKey: 'settings.project_details_form.amount_raised_label',
    type: 'number',
    placeholderKey: 'settings.project_details_form.amount_raised_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'amountRaisedToDate'
  },
  {
    name: 'totalBudget',
    labelKey: 'settings.project_details_form.total_budget_label',
    type: 'number',
    placeholderKey: 'settings.project_details_form.total_budget_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'projectTimeline',
    sectionTitleKey: 'settings.project_details_form.org_address_title',
    key: 'totalProjectBudget'
  },

  // Project Content Section
  {
    name: 'videoUrl',
    labelKey: 'settings.project_details_form.project_video_title',
    type: 'file',
    defaultValue: null,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'projectVideo'
  },
  {
    name: 'coverPhoto',
    labelKey: 'settings.project_details_form.cover_photo_title',
    type: 'file',
    defaultValue: null,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'projectCoverImageDto'
  },
  {
    name: 'description',
    labelKey: 'settings.project_details_form.description_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.description_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'description'
  },
  {
    name: 'problem',
    labelKey: 'settings.project_details_form.problem_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.problem_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'problem'
  },
  {
    name: 'solutions',
    labelKey: 'settings.project_details_form.solutions_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.solutions_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'solution'
  },
  {
    name: 'activities',
    labelKey: 'settings.project_details_form.activities_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.activities_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'projectContent',
    sectionTitleKey: 'settings.project_details_form.project_content_title',
    key: 'activities'
  },

  // ==================== TEAM TAB ====================
  // Team Overview Section
  {
    name: 'teamSummary',
    labelKey: 'settings.project_details_form.team_summary_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.team_summary_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'team',
    group: 'teamOverview',
    sectionTitleKey: 'settings.project_details_form.team_section_title',
    key: 'teamSummary'
  },
  {
    name: 'projectManager',
    labelKey: 'settings.project_details_form.project_manager_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.project_manager_placeholder',
    defaultValue: null,
    tab: 'team',
    group: 'teamOverview',
    sectionTitleKey: 'settings.project_details_form.team_section_title',
    key: 'projectManager'
  },

  // Team Member (FormArray item)
  {
    name: 'nameTitle',
    labelKey: 'settings.project_details_form.team_member_name_title_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.team_member_name_title_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'teamMember',
    sectionTitleKey: 'settings.project_details_form.team_members_title',
    key: 'fullName'
  },
  {
    name: 'email',
    labelKey: 'settings.project_details_form.team_member_email_label',
    type: 'email',
    placeholderKey: 'settings.project_details_form.team_member_email_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'teamMember',
    sectionTitleKey: 'settings.project_details_form.team_members_title',
    key: 'email'
  },
  {
    name: 'bio',
    labelKey: 'settings.project_details_form.team_member_bio_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.team_member_bio_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(500)],
    tab: 'team',
    group: 'teamMember',
    sectionTitleKey: 'settings.project_details_form.team_members_title',
    key: 'bio'
  },
  {
    name: 'photoUrl',
    labelKey: '',
    type: 'text',
    defaultValue: '',
    tab: 'team',
    group: 'teamMember',
    sectionTitleKey: 'settings.project_details_form.team_members_title',
    key: 'profilePicture'
  },
  {
    name: 'photoUuid',
    labelKey: '',
    type: 'text',
    defaultValue: '',
    tab: 'team',
    group: 'teamMember',
    sectionTitleKey: 'settings.project_details_form.team_members_title',
    key: 'profilePictureUuid'
  },

  // ==================== GOALS TAB ====================
  // Goals Overview Section
  {
    name: 'resultsMeasurement',
    labelKey: 'settings.project_details_form.results_measurement_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.results_measurement_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'goals',
    group: 'goalsOverview',
    sectionTitleKey: 'settings.project_details_form.goals_section_title',
    key: 'resultsMeasurement'
  },

  // ==================== GALLERY TAB ====================
  // Gallery Overview Section
  {
    name: 'galleryImages',
    labelKey: 'settings.project_details_form.gallery_images_label',
    type: 'file',
    defaultValue: null,
    tab: 'gallery',
    group: 'galleryOverview',
    sectionTitleKey: 'settings.project_details_form.gallery_section_title',
    key: 'projectImages'
  },

  // Partner Organization (FormArray item)
  {
    name: 'name',
    labelKey: 'settings.project_details_form.partner_org_name_label',
    type: 'text',
    placeholderKey: 'settings.project_details_form.partner_org_name_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'partnerOrg',
    sectionTitleKey: 'settings.project_details_form.partner_orgs_title',
    key: 'name'
  },
  {
    name: 'description',
    labelKey: 'settings.project_details_form.partner_org_description_label',
    type: 'textarea',
    placeholderKey: 'settings.project_details_form.partner_org_description_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(500)],
    maxLength: 500,
    tab: 'team',
    group: 'partnerOrg',
    sectionTitleKey: 'settings.project_details_form.partner_orgs_title',
    key: 'relationshipDescription'
  }
];

/**
 * Helper: get fields by group
 */
export function getProjectFieldsByGroup(group: ProjectFormGroupName): ProjectFormFieldConfig[] {
  return PROJECT_FORM_CONFIG.filter(f => f.group === group);
}

/**
 * Section a field is displayed under in the profile setup dialog.
 * Falls back to the form-model group when no display override is set.
 */
export function getProjectProfileGroup(field: ProjectFormFieldConfig): ProjectFormGroupName {
  return field.profileGroup ?? field.group;
}

/**
 * Helper: get fields for a profile setup dialog section
 */
export function getProjectProfileFieldsByGroup(group: ProjectFormGroupName): ProjectFormFieldConfig[] {
  return PROJECT_FORM_CONFIG.filter(f => getProjectProfileGroup(f) === group);
}

/**
 * Helper: get unique sections (by group) for a given tab
 */
export function getProjectSectionsForTab(tab: ProjectFormTab): { id: string; title: string; group: ProjectFormGroupName }[] {
  const seenGroups = new Set<ProjectFormGroupName>();
  const sections: { id: string; title: string; group: ProjectFormGroupName }[] = [];

  PROJECT_FORM_CONFIG
    .filter(f => f.tab === tab)
    .forEach(field => {
      const group = getProjectProfileGroup(field);
      if (!seenGroups.has(group)) {
        seenGroups.add(group);
        sections.push({ id: group, title: field.sectionTitleKey, group: group });
      }
    });

  return sections;
}

/**
 * Helper: build FormGroup structure for the details tab
 */
export function buildProjectFormGroupStructure(): any {
  const structure: any = {};
  const groups = [...new Set(PROJECT_FORM_CONFIG.filter(f => f.tab === 'details').map(f => f.group))];

  groups.forEach(group => {
    const groupFields = getProjectFieldsByGroup(group);
    const groupControls: any = {};
    groupFields.forEach(field => {
      groupControls[field.name] = [field.defaultValue, field.validators || []];
    });
    structure[group] = groupControls;
  });

  return structure;
}

/**
 * Helper: build FormGroup structure for the team overview section
 */
export function buildTeamFormGroupStructure(): any {
  const structure: any = {};
  const overviewFields = PROJECT_FORM_CONFIG.filter(f => f.group === 'teamOverview');
  const controls: any = {};
  overviewFields.forEach(field => {
    controls[field.name] = [field.defaultValue, field.validators || []];
  });
  structure['teamOverview'] = controls;
  return structure;
}

/**
 * Helper: build controls for a single team member FormGroup
 */
export function buildTeamMemberControls(): any {
  const fields = PROJECT_FORM_CONFIG.filter(f => f.group === 'teamMember');
  const controls: any = { uuid: [null] };
  fields.forEach(field => {
    controls[field.name] = [field.defaultValue, field.validators || []];
  });
  return controls;
}

/**
 * Helper: build FormGroup structure for the goals overview section
 */
export function buildGoalsFormGroupStructure(): any {
  const structure: any = {};
  const overviewFields = PROJECT_FORM_CONFIG.filter(f => f.group === 'goalsOverview');
  const controls: any = {};
  overviewFields.forEach(field => {
    controls[field.name] = [field.defaultValue, field.validators || []];
  });
  structure['goalsOverview'] = controls;
  return structure;
}

/**
 * Helper: build FormGroup structure for the gallery overview section
 */
export function buildGalleryFormGroupStructure(): any {
  const structure: any = {};
  const overviewFields = PROJECT_FORM_CONFIG.filter(f => f.group === 'galleryOverview');
  const controls: any = {};
  overviewFields.forEach(field => {
    controls[field.name] = [field.defaultValue, field.validators || []];
  });
  structure['galleryOverview'] = controls;
  return structure;
}

/**
 * Helper: build controls for a single partner organization FormGroup
 */
export function buildPartnerOrgControls(): any {
  const fields = PROJECT_FORM_CONFIG.filter(f => f.group === 'partnerOrg');
  const controls: any = {};
  fields.forEach(field => {
    controls[field.name] = [field.defaultValue, field.validators || []];
  });
  return controls;
}
